import { CSSProperties, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    pointerWithin,
    rectIntersection,
    CollisionDetection,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EyeOff, GripVertical } from 'lucide-react';

import { cn } from '@/lib/utils';
import { RoomWidgetId, RoomZone, VisualizerLayout, WidgetPlacement } from './layout.schema';
import { hasTopBar } from './layout.normalize';
import { WIDGET_META, ZONE_LABEL_KEYS } from './layout.meta';
import { isDarkColor } from './PresetPicker';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';

export type BoardTarget = RoomZone | 'hidden';

const HIDDEN = 'zone:hidden';

/** Pointer first (exact), then overlap — so a chip dropped on an empty zone still lands. */
const collision: CollisionDetection = (args) => {
    const within = pointerWithin(args);
    return within.length ? within : rectIntersection(args);
};

function Chip({ placement, selected, onSelect, dark }: { placement: WidgetPlacement; selected: boolean; onSelect: () => void; dark?: boolean }) {
    const { t } = useTranslation();
    const meta = WIDGET_META[placement.id];
    const Icon = meta.icon;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: placement.id });
    const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition };
    return (
        <button
            ref={setNodeRef}
            type="button"
            style={style}
            {...attributes}
            {...listeners}
            onClick={onSelect}
            className={cn(
                'inline-flex h-6 max-w-full cursor-grab items-center gap-1 whitespace-nowrap rounded-md border bg-background px-1.5 text-[11px] leading-none text-foreground shadow-xs transition-colors active:cursor-grabbing',
                'hover:border-foreground/40',
                selected && 'border-foreground ring-1 ring-foreground',
                !placement.visible && 'text-muted-foreground',
                isDragging && 'opacity-30',
                dark && !selected && 'border-white/20',
            )}
        >
            <Icon className="size-3 shrink-0" />
            <span className="truncate">{t(meta.labelKey)}</span>
        </button>
    );
}

function Zone({
    id,
    label,
    items,
    selected,
    onSelect,
    className,
    dark,
    vertical,
    style,
}: {
    id: string;
    label: string;
    items: WidgetPlacement[];
    selected: RoomWidgetId | null;
    onSelect: (id: RoomWidgetId) => void;
    className?: string;
    dark?: boolean;
    vertical?: boolean;
    style?: CSSProperties;
}) {
    const { setNodeRef, isOver } = useDroppable({ id });
    return (
        <SortableContext items={items.map((w) => w.id)} strategy={rectSortingStrategy}>
            <div
                ref={setNodeRef}
                title={label}
                style={style}
                className={cn(
                    'flex min-h-8 min-w-0 flex-wrap content-start items-start gap-1 rounded-md border border-dashed p-1 transition-colors',
                    vertical && 'flex-col items-stretch',
                    dark ? 'border-white/25' : 'border-foreground/20',
                    isOver && (dark ? 'border-white bg-white/10' : 'border-foreground/70 bg-foreground/[0.06]'),
                    className,
                )}
            >
                {items.length === 0 && (
                    <span className={cn('pointer-events-none select-none px-0.5 py-1 text-[10px] leading-tight', dark ? 'text-white/55' : 'text-muted-foreground')}>
                        {label}
                    </span>
                )}
                {items.map((w) => (
                    <Chip key={w.id} placement={w} selected={selected === w.id} onSelect={() => onSelect(w.id)} dark={dark} />
                ))}
            </div>
        </SortableContext>
    );
}

/**
 * The room view drawn as a map of its zones — top bar, side panel header,
 * and the six places over the photo — with each widget a chip that can be
 * dragged anywhere, or into "Hidden" to take it off the page. The same moves
 * can be made by dragging on the live preview; both edit the same draft.
 */
export function ZoneBoard({
    layout,
    selected,
    onSelect,
    onMove,
}: {
    layout: VisualizerLayout;
    selected: RoomWidgetId | null;
    onSelect: (id: RoomWidgetId) => void;
    onMove: (id: RoomWidgetId, target: BoardTarget, beforeId: RoomWidgetId | null) => void;
}) {
    const { t } = useTranslation();
    const [activeId, setActiveId] = useState<RoomWidgetId | null>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const desk = layout.room.desktop;
    // The assistant's button exists only for vendors a super admin granted the assistant.
    const { hasFeature } = useVendorPlan();
    const widgets = hasFeature('smartAssistant') ? desk.widgets : desk.widgets.filter((w) => w.id !== 'assistant');
    const inZone = (zone: RoomZone) => widgets.filter((w) => w.visible && w.zone === zone);
    const hidden = widgets.filter((w) => !w.visible);
    const bg = desk.canvas.background ?? '#ECEAE6';
    const dark = isDarkColor(bg);
    const bar = hasTopBar(layout);
    const barFloating = desk.topBar.style !== 'attached';
    const panelFloating = desk.panel.style === 'floating';

    const zoneOf = (id: string): BoardTarget | null => {
        const w = widgets.find((x) => x.id === id);
        if (!w) return null;
        return w.visible ? w.zone : 'hidden';
    };

    const handleDragStart = (e: DragStartEvent) => setActiveId(e.active.id as RoomWidgetId);
    const handleDragEnd = (e: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = e;
        if (!over) return;
        const id = active.id as RoomWidgetId;
        const overId = String(over.id);
        let target: BoardTarget | null;
        let beforeId: RoomWidgetId | null = null;
        if (overId === HIDDEN) target = 'hidden';
        else if (overId.startsWith('zone:')) target = overId.slice(5) as RoomZone;
        else {
            target = zoneOf(overId);
            if (!target) return;
            const list = target === 'hidden' ? hidden : inZone(target);
            const from = list.findIndex((w) => w.id === id);
            const to = list.findIndex((w) => w.id === overId);
            // Dragging forward within a zone lands after the chip under the pointer.
            beforeId = from >= 0 && from < to ? (list[to + 1]?.id ?? null) : (overId as RoomWidgetId);
        }
        if (!target) return;
        if (target === zoneOf(id) && (beforeId === id)) return;
        onMove(id, target, beforeId);
    };

    const zone = (z: RoomZone, extra?: { className?: string; vertical?: boolean; onDark?: boolean; style?: CSSProperties }) => (
        <Zone
            id={`zone:${z}`}
            label={t(ZONE_LABEL_KEYS[z])}
            items={inZone(z)}
            selected={selected}
            onSelect={onSelect}
            className={extra?.className}
            vertical={extra?.vertical}
            dark={extra?.onDark}
            style={extra?.style}
        />
    );
    // The top bar's three zones share its width by how much each holds, as the real bar does.
    const barGrow = (z: RoomZone): CSSProperties => ({ flex: `${Math.max(1, inZone(z).length)} 1 0`, minWidth: inZone(z).length ? 104 : 72 });

    const panel: ReactNode = (
        <div
            className={cn(
                'flex w-[27%] shrink-0 flex-col gap-1.5 bg-background p-1.5',
                panelFloating ? 'm-1.5 rounded-lg shadow-sm ring-1 ring-foreground/10' : desk.panel.side === 'left' ? 'border-e' : 'border-s',
            )}
        >
            {zone('panel.header', { vertical: false })}
            <div className="grid flex-1 content-start gap-1" style={{ gridTemplateColumns: `repeat(${Math.min(4, desk.panel.catalogColumns)}, minmax(0, 1fr))` }}>
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="aspect-square rounded-sm bg-foreground/[0.06]" />
                ))}
            </div>
        </div>
    );

    const active = activeId ? widgets.find((w) => w.id === activeId) : null;

    return (
        <DndContext sensors={sensors} collisionDetection={collision} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
            <div className="flex flex-col gap-2.5">
                <div
                    className="relative flex min-h-[300px] overflow-hidden rounded-lg border"
                    style={{ backgroundColor: bg, flexDirection: desk.panel.side === 'left' ? 'row' : 'row-reverse' }}
                >
                    {panel}
                    <div className="relative flex min-w-0 flex-1 flex-col">
                        {/* top bar */}
                        <div
                            className={cn(
                                'flex flex-wrap gap-1 p-1',
                                barFloating ? 'm-1.5 rounded-md shadow-sm ring-1 ring-foreground/10' : 'mx-2 rounded-b-md',
                                bar ? (desk.topBar.style === 'glass' ? 'bg-background/80 backdrop-blur' : 'bg-background') : 'bg-background/40',
                            )}
                        >
                            {zone('topbar.start', { style: barGrow('topbar.start') })}
                            {zone('topbar.center', { className: 'justify-center', style: barGrow('topbar.center') })}
                            {zone('topbar.end', { className: 'justify-end', style: barGrow('topbar.end') })}
                        </div>
                        {/* over the photo */}
                        <div className="grid flex-1 grid-cols-3 grid-rows-[auto_1fr_auto] gap-1.5 p-2">
                            {zone('canvas.topLeft', { onDark: dark })}
                            {zone('canvas.topCenter', { className: 'justify-center', onDark: dark })}
                            {zone('canvas.topRight', { className: 'justify-end', onDark: dark })}
                            <div className="col-span-3 flex items-center justify-center">
                                <span className={cn('select-none text-[10px] uppercase tracking-wide', dark ? 'text-white/35' : 'text-foreground/25')}>
                                    {t('VISUALIZER_LAYOUT.BOARD.ROOM_PHOTO')}
                                </span>
                            </div>
                            {zone('canvas.bottomLeft', { onDark: dark })}
                            {zone('canvas.bottomCenter', { className: 'justify-center', onDark: dark })}
                            {zone('canvas.bottomRight', { className: 'justify-end', onDark: dark })}
                        </div>
                    </div>
                </div>

                {/* hidden tray */}
                <div className="flex items-start gap-2">
                    <span className="mt-1.5 flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                        <EyeOff className="size-3.5" />
                        {t('VISUALIZER_LAYOUT.BOARD.HIDDEN')}
                    </span>
                    <Zone
                        id={HIDDEN}
                        label={t('VISUALIZER_LAYOUT.BOARD.HIDDEN_EMPTY')}
                        items={hidden}
                        selected={selected}
                        onSelect={onSelect}
                        className="flex-1"
                    />
                </div>
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <GripVertical className="size-3" />
                    {t('VISUALIZER_LAYOUT.BOARD.HINT')}
                </p>
            </div>

            <DragOverlay dropAnimation={null}>
                {active ? (
                    <span className="inline-flex h-6 items-center gap-1 rounded-md border border-foreground bg-background px-1.5 text-[11px] text-foreground shadow-md">
                        {(() => { const Icon = WIDGET_META[active.id].icon; return <Icon className="size-3" />; })()}
                        {t(WIDGET_META[active.id].labelKey)}
                    </span>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
